<template>
  <section class="kws-quiz-question" v-bind="parseAttributeString">
    <div class="kws-quiz-text-wrapper">
      <h2 class="h2" v-if="headline">{{ headline }}</h2>
      <h2 class="h2" v-if="!headline" v-html="headlineText"></h2>
      <first-spirit-image
        v-if="img && img.sm"
        class="kws-quiz-img-mobile"
        :fs-cc-attrs="img.fsCcAttrs"
        :alt="img.alt"
        :src="img.sm.src"
        :srcset="img.sm.srcset"
        :sizes="img.sm.sizes"
      ></first-spirit-image>
      <div class="kws-quiz-text-content">
        <p class="">{{ text }}</p>
        <component
          v-if="!solutionIsRevealed"
          :is="answers.type"
          :options="answers.options"
          :question-id="id"
          @change="selectAnswer"
        ></component>
        <solution
          v-if="solutionIsRevealed && answers.solution"
          :options="answers.options"
          :solution="answers.solution"
        ></solution>
        <button
          class="button button--medium button--back"
          @click="goBack" v-if="!disableBackButton && !first">
          {{ translate('btnBack') }}
        </button>
        <button
          class="button button--medium button--primary"
          @click="revealSolution" v-if="revealButtonAvailable"
          :disabled="!answerSelected"
        >{{ translate('btnReveal') }}
        </button>
        <button
          class="button button--medium button--primary"
          @click="goOn"
          v-if="nextButtonAvailable"
          :disabled="!answerSelected"
        >{{ translate('btnNext') }}
        </button>
        <button
          class="button button--medium button--primary"
          @click="evaluateResult"
          v-if="lastButtonAvailable"
          :disabled="!answerSelected"
        >{{ translate('btnResult') }}
        </button>
      </div>
    </div>
    <div class="kws-quiz-img-desktop">
      <first-spirit-image
        v-if="img && img.lg"
        class="kws-quiz-img-desktop"
        :fs-cc-attrs="img.fsCcAttrs"
        :alt="img.alt"
        :src="img.lg.src"
        :srcset="img.lg.srcset"
        :sizes="img.lg.sizes"
      ></first-spirit-image>
    </div>
  </section>
</template>

<script>
import { localizeContent } from '@/shared/Utils';
import Checkbox from './answer-type-checkbox.vue';
import Radio from './answer-type-radio.vue';
import Dropdown from './answer-type-dropdown.vue';
import FreeText from './answer-type-text.vue';
import Solution from './solution.vue';
import FirstSpiritContentCreatorMixin from '../../shared/mixins/FirstSpiritContentCreatorMixin';
import FirstSpiritImage from './fs-image.vue';

export default {
  // eslint-disable-next-line
  name: 'question',
  mixins: [FirstSpiritContentCreatorMixin],
  components: {
    Checkbox,
    Radio,
    Dropdown,
    FreeText,
    Solution,
    FirstSpiritImage,
  },
  props: {
    id: String,
    step: Number,
    total: Number,
    headline: String,
    text: String,
    img: Object,
    answers: Object,
    first: Boolean,
    last: Boolean,
    disableBackButton: Boolean,
    provideSolution: Boolean,
  },
  data: () => ({
    solutionIsRevealed: false,
    answerSelected: null,
    localeLanguage: null,
  }),
  computed: {
    nextButtonAvailable() {
      return !this.last && (!this.provideSolution || this.solutionIsRevealed);
    },
    revealButtonAvailable() {
      return this.provideSolution && !this.solutionIsRevealed;
    },
    lastButtonAvailable() {
      return this.last && (!this.provideSolution || this.solutionIsRevealed);
    },
    headlineText() {
      return `${localizeContent(this.step)} <small>${this.translate('lblOf')} ${localizeContent(this.total)}</small>`;
    },
  },
  methods: {
    goBack() {
      this.$emit('go-back');
    },
    revealSolution() {
      this.solutionIsRevealed = true;
    },
    goOn() {
      this.solutionIsRevealed = false;
      this.$emit('go-on');
    },
    selectAnswer(updatedOptions) {
      // eslint-disable-next-line
      this.answers.options = updatedOptions;
      this.activateBtnNext();
    },
    activateBtnNext() {
      this.answerSelected = this.answers.options.some((o) => o.selected);
    },
    evaluateResult() {
      this.$emit('evaluate-result');
    },
  },
  watch: {
    id() {
      // does not fire for the first question (or last, if going back)
      this.answerSelected = this.answers.options && this.answers.options.some((o) => o.selected);
    },
  },
  created() {
    // fires only for the first question (or last, if going back)
    this.answerSelected = this.answers.options && this.answers.options.some((o) => o.selected);
    this.localeLanguage = document.documentElement.lang || 'en';
  },
};
</script>

<style lang="scss">

.kws-quiz-question {
  background-color: rgba(255, 255, 255, .75);
  color: $color-black;

  ul {
    list-style: none;
    padding-block: 0 rem(20px);
    padding-inline: 0 0;
    margin-block: rem(-10px) 0;
    margin-inline: 0;

    li {
      display: block;
      padding: 0;
      margin: 0;
    }

    input {
      display: none;
    }

    label {
      display: block;
      padding-inline: rem(32px) 0;
      padding-block: rem(10px) 0;
      position: relative;
    }
  }

  .button--back {
    order: 1;
    margin-top: rem(15px);
  }

  .explanation {
    font-size: rem(15px);
    line-height: rem(18px);
  }
}

// tablet
@media (min-width: rem(664px)) {
  .kws-quiz-question {
    .button--back {
      order: 1;
      margin-top: 0;
      margin-inline-end: rem(20px);
    }
  }
}
</style>
